import React, { useState, useEffect, useCallback } from "react";
import NavigationSidebar from "../components/Navigationsidebar/NavigationSidebar";
import Header from "../components/Header";
import FilterSidebar from "../components/FilterSidebar/FilterSidebar";
import { useFilter } from "../FilterContext";
import { FILTER_CONFIG } from "../config/filterconfig";
import jsonData from "../current-survey-openText.json";
import insightsData from "../merged_all_insights.json";
import "./D10_Open_Text_Responses.css";

const qNumOptions = [
  { value: "MQ0305", label: "What Makes this Company a Great Place to Work" },
  {
    value: "MQ0156",
    label: "What Would Make this Place a Better Place to Work",
  },
  {
    value: "MQ0160",
    label: "What Would you Like to Tell the Senior Leadership Team",
  },
];

const D10_Open_Text_ResponsesTable = () => {
  const { filterCriteria, setFilterCriteria } = useFilter();
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [questionNumber, setQuestionNumber] = useState(qNumOptions[0].value);
  const [theme, setTheme] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [themes, setThemes] = useState([]);
  const [narrationSummary, setNarrationSummary] = useState("");

  // Add refresh page functionality
  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  // Generate filter options for FilterSidebar
  useEffect(() => {
    const generateFilterOptions = () => {
      const options = {};
      FILTER_CONFIG.forEach(({ fieldName, jsonKey, sortOrder }) => {
        let uniqueValues = [...new Set(jsonData.map((item) => item[jsonKey]))];
        uniqueValues = uniqueValues.filter((value) => value !== null);

        if (sortOrder) {
          uniqueValues = uniqueValues.sort(
            (a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b)
          );
        } else {
          uniqueValues.sort((a, b) =>
            a?.localeCompare(b, undefined, { sensitivity: "base" })
          );
        }

        options[fieldName] = uniqueValues;
      });
      setFilterOptions(options);
    };

    generateFilterOptions();
  }, []);

  // Dynamically update themes based on results
  useEffect(() => {
    const updateThemesBasedOnResults = () => {
      const uniqueThemes = new Set();

      jsonData.forEach((item) => {
        if (item.q_num === questionNumber) {
          for (let i = 1; i <= 8; i++) {
            const themeKey = `theme_${i.toString().padStart(2, "0")}`;
            const themeValue = item[themeKey];

            if (
              themeValue &&
              themeValue.trim().toLowerCase() !== "na" &&
              item.open_response
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase())
            ) {
              uniqueThemes.add(themeValue.trim());
            }
          }
        }
      });

      const filteredThemes = Array.from(uniqueThemes)
        .map((theme) => theme.charAt(0).toUpperCase() + theme.slice(1))
        .sort();

      setThemes(filteredThemes);
    };

    updateThemesBasedOnResults();
  }, [searchTerm, questionNumber, filterCriteria]);

  // Filter data based on criteria
  useEffect(() => {
    const filtered = jsonData.filter((item) => {
      const matchesQuestion = item.q_num === questionNumber;
      const matchesSearchTerm = item.open_response
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesTheme =
        theme === "" ||
        Object.keys(item)
          .filter((key) => key.startsWith("theme_"))
          .some(
            (key) => item[key]?.trim().toLowerCase() === theme.toLowerCase()
          );

      return (
        matchesQuestion &&
        matchesSearchTerm &&
        Object.keys(filterCriteria).every((key) => {
          const filterValues = filterCriteria[key];
          const itemValue = item[key];
          return filterValues.length === 0 || filterValues.includes(itemValue);
        }) &&
        matchesTheme
      );
    });
    setFilteredData(filtered);
  }, [filterCriteria, searchTerm, questionNumber, theme]);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const toggleFilterSidebar = () => setIsFilterSidebarOpen((prev) => !prev);
  const handleSearch = (event) => setSearchTerm(event.target.value);
  const handleQuestionChange = (event) => setQuestionNumber(event.target.value);
  const handleThemeChange = (event) => {
    const selectedTheme = event.target.value;
    if (!themes.includes(selectedTheme)) {
      setTheme("");
    } else {
      setTheme(selectedTheme);
    }
  };

  const downloadCSV = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [["Response"], ...filteredData.map((item) => [item.open_response])]
        .map((row) => row.map((field) => `"${field}"`).join(","))
        .join("\n");

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "text_responses.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateNarrationSummary = () => {
    const summary = `
      Our organizational insights reveal a commitment to fostering a collaborative workplace culture. Leadership visibility, communication, and professional development emerge as key themes. Employees value tailored training and fair compensation.

      From a demographic perspective, Millennials and Generation Z prioritize growth and technology, while Generation X and Baby Boomers emphasize stability and benefits. Long-tenured employees focus on retaining experience, while newer hires value mentorship and onboarding.

      Departmentally, Customer Affairs highlights strong teamwork, while areas like Field Services suggest opportunities for cross-functional collaboration.

      This narrative underscores a workplace vision of inclusivity, transparency, and innovation.

      What type of activities would you like to see in order to learn more about our company and its functional areas?

      Respondents suggest activities like job shadowing, functional area showcases, and interactive webinars to improve understanding of our company's operations and functional areas. Many employees desire greater transparency and hands-on opportunities for learning about other departments.
    `;

    setNarrationSummary(summary.trim());
  };

  return (
    <div className="D10_Open_Text_Responses-dashboard-container">
      <Header
        title="Open Text Responses"
        toggleSidebar={toggleSidebar}
        toggleFilterSidebar={toggleFilterSidebar}
        refreshPage={refreshPage}
      />
      <NavigationSidebar
        isOpen={isSidebarOpen}
        className={isSidebarOpen ? "sidebar" : "sidebar-hidden"}
      />
      <FilterSidebar
        isOpen={isFilterSidebarOpen}
        filterOptions={filterOptions}
        setFilterCriteria={setFilterCriteria}
      />
      <div className="D10_Open_Text_Responses-main-content">
        <div className="D10_Open_Text_Responses-app-container">
          <div className="D10_Open_Text_Responses-controls">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
              className="searchbar"
            />
            <select
              value={questionNumber}
              onChange={handleQuestionChange}
              className="searchbar2"
            >
              {qNumOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={theme}
              onChange={handleThemeChange}
              className="searchbar2"
            >
              <option value="">All Themes</option>
              {themes.map((themeOption, index) => (
                <option key={index} value={themeOption}>
                  {themeOption}
                </option>
              ))}
            </select>
            <button onClick={downloadCSV} className="download-button">
              Download CSV
            </button>
            <button
              onClick={generateNarrationSummary}
              className="narration-button"
            >
              Narrative Insight
            </button>
          </div>
          <div className="D10_Open_Text_Responses-scrollable-table">
            <table className="D10_Open_Text_Responses-centered-table">
              <thead>
                <tr>
                  <th>Response</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.open_response}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {narrationSummary && (
            <>
              <div
                className="D10_Open_Text_Responses-overlay"
                onClick={() => setNarrationSummary("")}
              ></div>
              <div className="D10_Open_Text_Responses-summary">
                <button
                  className="D10_Open_Text_Responses-summary-close"
                  onClick={() => setNarrationSummary("")}
                >
                  ×
                </button>
                <h3>Narrative Insight</h3>
                <p>{narrationSummary}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default D10_Open_Text_ResponsesTable;